import { createURL } from '@sm/utils';
import { t } from '@sm/intl';
import NAVBARCOPY from '../NavBarCopy';

const defaultMyTeamSubItems = {
  teamSummaryTab: false,
  manageUsersTab: false,
  addUsersTab: false,
  adminDashboardTab: false,
  divisionsTab: false,
  workgroupsTab: false,
  libraryTab: true,
  activityTab: false,
  offlineDevicesTab: false,
  manageCreditsTab: false,
  settingsTab: false
};
const ACCOUNT_TYPES = {
  enterpriseAccount: 'enterpriseAccount',
  teamAccount: 'teamAccount'
};
const USER_ROLES = {
  accountOwner: 'accountOwner',
  admin: 'admin',
  divisionAdmin: 'divisionAdmin',
  user: 'user'
};

// build the submenu items based on the current user package and role permissions
const getSubMenuItems = menuConfigs => {
  const menuItems = [
    menuConfigs.canAccess.teamSummaryTab && {
      path: createURL('/team/', { ut_source: 'header' }),
      name: t(NAVBARCOPY.TEAM_SUMMARY)
    },
    menuConfigs.canAccess.manageUsersTab && {
      path: createURL('/team/users/', { ut_source: 'header' }),
      name: t(NAVBARCOPY.MANAGE_USERS)
    },
    menuConfigs.canAccess.addUsersTab && {
      path: createURL('/team/add', { ut_source: 'header' }),
      name: t(NAVBARCOPY.ADD_USERS)
    },
    menuConfigs.canAccess.adminDashboardTab && {
      path: createURL('/team/manage/users', { ut_source: 'header' }),
      name: t(NAVBARCOPY.ADMIN_DASHBOARD)
    },
    menuConfigs.canAccess.divisionsTab && {
      path: createURL('/team/divisions', { ut_source: 'header' }),
      name: t(NAVBARCOPY.DIVISIONS)
    },
    menuConfigs.canAccess.workgroupsTab && {
      path: createURL('/team/workgroups/all', { ut_source: 'header' }),
      name: t(NAVBARCOPY.WORKGROUPS)
    },
    menuConfigs.canAccess.libraryTab && {
      path: createURL('/team/library/', { ut_source: 'header' }),
      name: t(NAVBARCOPY.LIBRARY)
    },
    menuConfigs.canAccess.activityTab && {
      path: createURL('/team/activity/', { ut_source: 'header' }),
      name: t(NAVBARCOPY.ACTIVITY)
    },
    menuConfigs.canAccess.offlineDevicesTab && {
      path: createURL('/team/devices', { ut_source: 'header' }),
      name: t(NAVBARCOPY.OFFLINE_DEVICES)
    },
    menuConfigs.canAccess.manageCreditsTab && {
      path: createURL('/team/manage_credits/', { ut_source: 'header' }),
      name: t(NAVBARCOPY.MANAGE_CREDITS)
    },
    menuConfigs.canAccess.settingsTab && {
      path: createURL('/team/settings', { ut_source: 'header' }),
      name: t(NAVBARCOPY.SETTINGS)
    }
  ];
  return menuItems.filter(Boolean);
};

// spell out all the combination of userPackage/role
// to better understand what the user can and cant see in the ui
const buildMenuConfigs = (
  features,
  isSSOEnabled = false,
  isAdminImpersonated = false
) => ({
  enterpriseAccount: {
    [`${USER_ROLES.accountOwner}`]: {
      canAccess: {
        ...defaultMyTeamSubItems,
        teamSummaryTab: true,
        manageUsersTab: true,
        addUsersTab: !isSSOEnabled, // admin cant add users via UI on SSO enabled account
        adminDashboardTab: features.adminDashboardEnabled,
        divisionsTab: features.divisionsEnabled,
        workgroupsTab: features.workgroupsEnabled,
        activityTab: features.auditLogsEnabled || isAdminImpersonated,
        offlineDevicesTab: features.offlineEnterpriseEnabled,
        manageCreditsTab: features.teamAudienceCreditsEnabled,
        settingsTab: true
      }
    },
    [`${USER_ROLES.admin}`]: {
      canAccess: {
        ...defaultMyTeamSubItems,
        teamSummaryTab: true,
        manageUsersTab: true,
        addUsersTab: !isSSOEnabled, // admin cant add users via UI on SSO enabled account
        adminDashboardTab: features.adminDashboardEnabled,
        divisionsTab: features.divisionsEnabled,
        workgroupsTab: features.workgroupsEnabled,
        activityTab: features.auditLogsEnabled || isAdminImpersonated,
        offlineDevicesTab: features.offlineEnterpriseEnabled
      }
    },
    [`${USER_ROLES.divisionAdmin}`]: {
      canAccess: {
        ...defaultMyTeamSubItems,
        adminDashboardTab: features.adminDashboardEnabled,
        divisionsTab: features.divisionsEnabled,
        workgroupsTab: features.workgroupsEnabled,
        activityTab: features.auditLogsEnabled || isAdminImpersonated
      }
    },
    [`${USER_ROLES.user}`]: {
      canAccess: {
        ...defaultMyTeamSubItems,
        workgroupsTab: features.workgroupsEnabled
      }
    }
  },
  teamAccount: {
    [`${USER_ROLES.accountOwner}`]: {
      canAccess: {
        ...defaultMyTeamSubItems,
        teamSummaryTab: true,
        manageUsersTab: true,
        addUsersTab: true
      }
    },
    [`${USER_ROLES.admin}`]: {
      canAccess: {
        ...defaultMyTeamSubItems,
        teamSummaryTab: true,
        manageUsersTab: true,
        addUsersTab: true
      }
    },
    [`${USER_ROLES.user}`]: {
      canAccess: {
        ...defaultMyTeamSubItems
      }
    }
  }
});

const getMenuItems = ({ user }, session) => {
  const team = user.team || {};
  const teamMembership = user.teamMembership || {};
  // I will work on conforming the mobile and desktop calls because
  // session is passed in in 2 different ways currently
  const userSession = session || user.session;
  const isAdminImpersonated = userSession && userSession.isAdminImpersonation;
  const { isAdmin, isDivisionAdmin } = teamMembership;
  const isSSOEnabled = team.ssoState === 'REQUIRED';
  const isTeamOwner = teamMembership.memberType === 'ACCOUNT_OWNER';
  const features = user.features || {};
  const isEnterpriseAccount = teamMembership.isEnterprise;
  const isTeamAccount = !teamMembership.isEnterprise;

  let userAccountType = '';
  if (isEnterpriseAccount) {
    userAccountType = ACCOUNT_TYPES.enterpriseAccount;
  } else if (isTeamAccount) {
    userAccountType = ACCOUNT_TYPES.teamAccount;
  }

  let userRole = '';
  if (isTeamOwner) {
    userRole = USER_ROLES.accountOwner;
  } else if (isAdmin) {
    userRole = USER_ROLES.admin;
  } else if (isDivisionAdmin) {
    userRole = USER_ROLES.divisionAdmin;
  } else {
    userRole = USER_ROLES.user;
  }

  if (!!userAccountType && !!userRole) {
    const menuConfigs = buildMenuConfigs(
      features,
      isSSOEnabled,
      isAdminImpersonated
    )[userAccountType][userRole];

    return getSubMenuItems(menuConfigs);
  }
  return [];
};

const getMyTeamMenuItems = (userData, session = {}) => {
  if (!userData || !userData.user) {
    return [];
  }

  return getMenuItems(userData, session);
};

export default getMyTeamMenuItems;
